/**
 * Per-hub share images (Open Graph / Twitter cards).
 *
 * Service and industry hub pages have no database `featured_image`, so without
 * this map they would fall back to the generic site card. Each entry points at a
 * 1200x630 crop of the page's own hero image (generated into
 * public/og-assets/hubs/), and the EN key is expanded to its ES and ZH-CN
 * localized paths so shared links in every language show the same visual.
 */
import { localizeHref } from "@/lib/localizeHref";
import { normalizePath } from "./hreflang";

/** EN hub path -> share card in /public. */
const EN_HUB_OG_IMAGES: Record<string, string> = {
  "/en/industries/combination-products": "/og-assets/hubs/industries-combination-products.jpg",
  "/en/industries/companion-diagnostics": "/og-assets/hubs/industries-companion-diagnostics.jpg",
  "/en/industries/in-vitro-diagnostics": "/og-assets/hubs/industries-in-vitro-diagnostics.jpg",
  "/en/industries/medical-devices": "/og-assets/hubs/industries-medical-devices.jpg",
  "/en/industries/medical-devices/medical-device-software":
    "/og-assets/hubs/industries-medical-devices-medical-device-software.jpg",
  "/en/industries/pharma-biotech": "/og-assets/hubs/industries-pharma-biotech.jpg",
  "/en/services/clinical-solutions": "/og-assets/hubs/services-clinical-solutions.jpg",
  "/en/services/lab-services": "/og-assets/hubs/services-lab-services.jpg",
  "/en/services/pharmacovigilance": "/og-assets/hubs/services-pharmacovigilance.jpg",
  "/en/services/qualification-validation": "/og-assets/hubs/services-qualification-validation.jpg",
  "/en/services/quality-assurance": "/og-assets/hubs/services-quality-assurance.jpg",
  "/en/services/regulatory-affairs": "/og-assets/hubs/services-regulatory-affairs.jpg",
  "/en/services/software-solutions-services":
    "/og-assets/hubs/services-software-solutions-services.jpg",
  "/en/services/regulatory-toxicology": "/og-assets/hubs/services-toxicology.jpg",
  // Overview / hub-level pages without a database featured image.
  "/en/services": "/og-assets/pages/services.jpg",
  "/en/solutions": "/og-assets/pages/solutions.jpg",
  "/en/news": "/og-assets/pages/news.jpg",
  "/en/resources": "/og-assets/pages/resources.jpg",
  "/en/knowledge-center": "/og-assets/pages/knowledge-center.jpg",
  "/en/request-your-audit": "/og-assets/pages/request-your-audit.jpg",
  "/en/newsletter": "/og-assets/pages/newsletter.jpg",
};

const buildMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [enPath, image] of Object.entries(EN_HUB_OG_IMAGES)) {
    for (const lang of ["en", "es", "zh-cn"] as const) {
      const localized = lang === "en" ? enPath : localizeHref(enPath, lang);
      if (!localized) continue;
      map[normalizePath(decodeURIComponent(localized))] = image;
      map[normalizePath(localized)] = image;
    }
  }
  return map;
};

export const HUB_OG_IMAGES: Record<string, string> = buildMap();

/** Share card for a hub path, or undefined when the path is not a hub. */
export const getHubOgImage = (path: string): string | undefined =>
  HUB_OG_IMAGES[normalizePath(path)];
